import axios from 'axios';
import React, { useEffect, useState, useRef, useCallback } from 'react'
import moment from 'moment';


import { Editor } from '@tinymce/tinymce-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HiOutlineTrash, HiOutlineDocumentRemove, HiOutlinePaperClip, HiOutlineClock, HiOutlineStar, HiOutlineCloudUpload, HiOutlineDotsVertical, HiOutlineX, HiOutlinePhotograph, HiOutlineDocument, HiOutlineFilm, HiOutlineMusicNote, HiOutlineReply } from 'react-icons/hi';
import { HiStar } from 'react-icons/hi';
import { BsPin, BsPinFill } from 'react-icons/bs';

axios.defaults.baseURL = "/api"

let tabIdCounter = 0;
const nextTabId = () => `tab-${++tabIdCounter}`;

const MAX_TABS = 10;

const Home = () => {

  // === Tab state ===
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const activeTab = tabs.find(t => t.id === activeTabId) || null;

  // Derived from active tab
  const note = activeTab?.note || {};
  const title = activeTab?.title || '';
  const text = activeTab?.text || '';
  const initialText = activeTab?.initialText || '';
  const category = activeTab?.category || 1;
  const attachments = activeTab?.attachments || [];
  const saved = activeTab?.saved || 0;
  const activeNote = activeTab?.noteId || null;

  // === Non-tab state ===
  const [categories, setCategories] = useState([])
  const [noteCounts, setNoteCounts] = useState({});
  const [notes, setNotes] = useState([])
  const [activeCategory, setActiveCategory] = useState(1);
  const [search, setSearch] = useState("");
  const [activeEditor, setActiveEditor] = useState(0);

  const [confirmAction, setConfirmAction] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showNoteMenu, setShowNoteMenu] = useState(false);

  const [loadingNotes, setLoadingNotes] = useState(0);
  const [loadingNote, setLoadingNote] = useState(0);
  const [loadingButton, setLoadingButton] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // null = not uploading, 0-100 = progress
  const [notesFadeTop, setNotesFadeTop] = useState(false);
  const [notesFadeBottom, setNotesFadeBottom] = useState(false);

  // === Tab helpers ===
  const updateTab = useCallback((tabId, updates) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, ...updates } : t));
  }, []);

  const updateActiveTab = useCallback((updates) => {
    if (!activeTabId) return;
    updateTab(activeTabId, updates);
  }, [activeTabId, updateTab]);

  const setTitle = useCallback((val) => updateActiveTab({ title: val }), [updateActiveTab]);
  const setText = useCallback((val) => updateActiveTab({ text: val }), [updateActiveTab]);
  const setCategory = useCallback((val) => updateActiveTab({ category: val }), [updateActiveTab]);
  const setAttachments = useCallback((val) => updateActiveTab({ attachments: val }), [updateActiveTab]);
  const setSaved = useCallback((val) => updateActiveTab({ saved: val }), [updateActiveTab]);
  const setNote = useCallback((val) => {
    if (typeof val === 'function') {
      setTabs(prev => prev.map(t => {
        if (t.id !== activeTabId) return t;
        return { ...t, note: val(t.note) };
      }));
    } else {
      updateActiveTab({ note: val });
    }
  }, [activeTabId, updateActiveTab]);

  const createTab = useCallback((noteId, noteData, attData) => {
    if (tabs.length >= MAX_TABS) {
      toast.error(`Maximum ${MAX_TABS} tabs`);
      return null;
    }
    const id = nextTabId();
    const newTab = {
      id,
      noteId,
      note: noteData || {},
      title: noteData?.title || '',
      text: noteData?.text || '',
      initialText: noteData?.text || '',
      category: noteData?.category_id || 1,
      attachments: attData || [],
      saved: 0,
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(id);
    return id;
  }, [tabs.length]);

  const switchTab = useCallback((tabId) => {
    if (tabId === activeTabId) return;
    // Save current editor HTML to current tab before switching
    if (activeTabId && editorRef.current) {
      const html = editorRef.current.getContent();
      updateTab(activeTabId, { initialText: html });
    }
    setActiveTabId(tabId);
    setShowNoteMenu(false);
    setActiveEditor(1);
  }, [activeTabId, updateTab]);

  const closeTab = useCallback((tabId, force = false) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    const lastSaved = lastSavedMapRef.current.get(tabId);
    const hasUnsaved = !force && tab.noteId && lastSaved &&
      (tab.text !== lastSaved.text || tab.title !== lastSaved.title);

    if (hasUnsaved) {
      setConfirmAction({
        icon: <HiOutlineDocumentRemove />,
        title: "Unsaved changes",
        message: "This tab has unsaved changes. Close anyway?",
        confirmText: "Close",
        danger: true,
        onConfirm: () => { removeTab(tabId); setConfirmAction(null); }
      });
    } else {
      removeTab(tabId);
    }
  }, [tabs]);

  const removeTab = useCallback((tabId) => {
    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== tabId);
      if (activeTabId === tabId) {
        const idx = prev.findIndex(t => t.id === tabId);
        const nextTab = newTabs[idx] || newTabs[idx - 1] || null;
        if (nextTab) {
          setActiveTabId(nextTab.id);
        } else {
          // Create a fresh empty tab instead of hiding the editor
          const id = nextTabId();
          const freshTab = {
            id,
            noteId: null,
            note: {},
            title: '',
            text: '',
            initialText: '',
            category: 1,
            attachments: [],
            saved: 0,
          };
          newTabs.push(freshTab);
          setActiveTabId(id);
          if (editorRef.current) editorRef.current.setContent('');
        }
      }
      return newTabs;
    });
    lastSavedMapRef.current.delete(tabId);
  }, [activeTabId]);

  // === End tab helpers ===

  const onFocus = () => {
    setActiveEditor(1)
    setSaved(0)
    setShowNoteMenu(false)
  }

  const timeAgo = (date) => {
    return moment.utc(date).local().fromNow();
  };

  useEffect(() => {

    const appHeight = () => {
        const doc = document.documentElement
        doc.style.setProperty('--app-height', `${window.innerHeight}px`)
    }
    window.addEventListener('resize', appHeight)

    appHeight()

    setCategories([{"id":1,"title":"Temporary","icon":"clock"},{"id":2,"title":"Important","icon":"star"},{"id":3,"title":"Trash","icon":"trash"}])

    const fetchNotes = async () => {
      try {
        const res = await axios.get(`/notes`)
        setNotes(res.data)
      } catch (err) {
        console.log(err)
      }
    }

    fetchNotes();
    fetchNoteCounts();

    // Create initial empty tab
    createTab(null, {}, []);

  }, []);

  // Fetch note counts
  const fetchNoteCounts = async () => {
    try {
      const res = await axios.get(`/notes/counts`);
      const counts = {};
      res.data.forEach(item => { counts[item.category_id] = item.count; });
      setNoteCounts(counts);
    } catch (err) {
      console.log(err);
    }
  };

  const NOTES_PER_PAGE = 20;

  const fetchNotesByCategory = async (activeCategory, showSkeleton = true) => {

    try {

      if (showSkeleton) {
        setLoadingNotes(1)
        setActiveEditor(0)
      }

      const [res] = await Promise.all([
        axios.get(`/notes/?category_id=${activeCategory}&limit=${NOTES_PER_PAGE}&offset=0`),
        showSkeleton ? new Promise(r => setTimeout(r, 300)) : Promise.resolve()
      ]);
      setNotes(res.data.notes)
      setHasMore(res.data.hasMore)

      setLoadingNotes(0)
      fetchNoteCounts();

    } catch (err) {
      console.log(err);
    }

  }

  const loadMoreNotes = async () => {
    if (loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const res = search
        ? await axios.post(`/notes/search/?search=${search}&limit=${NOTES_PER_PAGE}&offset=${notes.length}`)
        : await axios.get(`/notes/?category_id=${activeCategory}&limit=${NOTES_PER_PAGE}&offset=${notes.length}`);
      setNotes(prev => [...prev, ...res.data.notes]);
      setHasMore(res.data.hasMore);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(()=>{
    if(activeCategory !== "") {
      const fetchData = async () => {
        fetchNotesByCategory(activeCategory);
      }
      fetchData();
    }
  }, [activeCategory])

  const searchMountedRef = useRef(false);
  useEffect(() => {
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return;
    }

    if(search === "") {
      clearEditor()
      if(activeCategory === 1) {
        fetchNotesByCategory(1);
      } else {
        setActiveCategory(1)
      }
    }

    const delayDebounceFn = setTimeout(async () => {

      setActiveEditor(0)

      try {

        setActiveCategory("")
        clearEditor()

        if(search !== "") {
          setLoadingNotes(1)
          const res = await axios.post(`/notes/search/?search=${search}&limit=${NOTES_PER_PAGE}&offset=0`)

          setNotes(res.data.notes)
          setHasMore(res.data.hasMore)
          setLoadingNotes(0)

        } else {
          setActiveCategory(1)
        }

      } catch (err) {
        console.log(err)
      }

    }, 800)

    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line
  }, [search])

  const getText = (html, symbols) =>{
    const doc = new DOMParser().parseFromString(html, "text/html")
    const text = doc.body.textContent
    var sep = ""

    if(text.length > symbols) {
      sep = ".."
    }
    return doc.body.textContent.substring(0, symbols) + sep;
  }

  const getNote = async (key) => {

    // Check if already open in a tab
    const existingTab = tabs.find(t => t.noteId === key);
    if (existingTab) {
      switchTab(existingTab.id);
      setActiveEditor(1);
      return;
    }

    try {
      setShowNoteMenu(false)
      setActiveEditor(1)

      // Save current editor state before switching
      if (activeTabId && editorRef.current) {
        updateTab(activeTabId, { initialText: editorRef.current.getContent() });
      }

      // Clear editor immediately so old content doesn't flash
      if (editorRef.current) {
        ignoreEditorChange.current = true;
        editorRef.current.setContent('');
      }

      const res = await axios.get(`/notes/${key}`);

      // Create/replace tab immediately with note data (empty attachments)
      const currentTab = tabs.find(t => t.id === activeTabId);
      let targetTabId;
      if (currentTab && !currentTab.noteId && !currentTab.title && !currentTab.text) {
        updateTab(activeTabId, {
          noteId: key,
          note: res.data,
          title: res.data.title,
          text: res.data.text,
          initialText: res.data.text,
          category: res.data.category_id,
          attachments: [],
          saved: 0,
        });
        targetTabId = activeTabId;
        lastSavedMapRef.current.set(activeTabId, { title: res.data.title, text: res.data.text });
      } else {
        targetTabId = createTab(key, res.data, []);
        if (targetTabId) {
          lastSavedMapRef.current.set(targetTabId, { title: res.data.title, text: res.data.text });
        }
      }

      // Load attachments after tab is shown
      try {
        const attRes = await axios.get(`/attachments/${key}`);
        if (attRes.data.length > 0) {
          updateTab(targetTabId, { attachments: attRes.data });
        }
      } catch (e) {}
    } catch (err) {
      console.log(err);
    }

  };

  const handleClick = useCallback(async (knownCategory = undefined, silent = false) => {

    if (!activeTab) return;

    try {

      var dbTitle = activeTab.title;
      const currentText = activeTab.text;
      let currentCategory = activeTab.category;
      const currentNote = activeTab.note;

      if(currentText !== "" || knownCategory !== undefined) {

        setLoadingButton(1)

        if(dbTitle === "") {
          dbTitle = 'Untitled note'
        }

        if(knownCategory !== undefined) {
          currentCategory = knownCategory
        }

        const dbObject = {
          title: dbTitle,
          text: currentText,
          category_id: currentCategory,
          date: new Date().toISOString().slice(0, 19).replace('T', ' '),
        }

        if (Object.keys(currentNote).length === 0) {
          const res = await axios.post(`/notes`, dbObject);
          // Fetch the created note and update tab
          const noteRes = await axios.get(`/notes/${res.data}`);
          updateActiveTab({
            noteId: res.data,
            note: noteRes.data,
            title: noteRes.data.title,
            initialText: noteRes.data.text,
            category: noteRes.data.category_id,
          });
          lastSavedMapRef.current.set(activeTabId, { title: noteRes.data.title, text: noteRes.data.text });
          if (!silent) toast.success('Note created');
        } else {
          await axios.put(`/notes/${currentNote.id}`, dbObject);
          lastSavedMapRef.current.set(activeTabId, { title: dbTitle, text: currentText });
          if (!silent) toast.success('Note saved');
        }

        if(currentCategory === activeCategory || knownCategory !== undefined) {

          fetchNotesByCategory(activeCategory, false);

          if(knownCategory !== undefined) {
            closeTab(activeTabId, true);
          }

        } else {
          setActiveCategory(currentCategory);
        }

        setLoadingButton(0)
        updateActiveTab({ saved: 1 });

        if(knownCategory !== undefined) {
          setActiveEditor(0)
        }

      }

    } catch (err) {
      console.log(err);
      toast.error('Failed to save');
    }

  }, [activeTab, activeTabId, activeCategory, updateActiveTab]);

  const deleteNote = (e) => {
    e.preventDefault();
    setConfirmAction({
      icon: <HiOutlineTrash />,
      title: "Delete note",
      message: "This note will be moved to trash. You can restore it within 30 days.",
      confirmText: "Move to trash",
      danger: true,
      onConfirm: () => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        handleClick(3, true);
        toast.success('Note moved to trash');
        setConfirmAction(null);
      }
    });
  };

  // Clear editor without creating a tab
  const clearEditor = () => {
    setActiveEditor(0);
  };

  const newNote = async (openEditor = undefined) => {
    // Save current editor state
    if (activeTabId && editorRef.current) {
      updateTab(activeTabId, { initialText: editorRef.current.getContent() });
    }

    createTab(null, {}, []);

    if(openEditor !== undefined) {
      setActiveEditor(1)
    }
  };

  const filterByCategory = async (key) => {
    setActiveCategory(key);
  };

  const handleClickRef = useRef(handleClick);
  useEffect(() => { handleClickRef.current = handleClick; }, [handleClick]);

  const fileInputRef = useRef(null);
  const attachmentsRef = useRef(null);
  const noteMenuRef = useRef(null);
  const [showFadeRight, setShowFadeRight] = useState(false);
  const [showFadeLeft, setShowFadeLeft] = useState(false);
  const editorRef = useRef(null);
  const ignoreEditorChange = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const lastSavedMapRef = useRef(new Map());

  // Sync editor content when switching tabs (avoid remounting TinyMCE)
  const prevTabIdRef = useRef(null);
  const prevInitialTextRef = useRef(null);
  useEffect(() => {
    if (!editorRef.current) return;
    const tabChanged = activeTabId !== prevTabIdRef.current;
    const contentChanged = initialText !== prevInitialTextRef.current;
    if (activeTabId && (tabChanged || contentChanged)) {
      editorRef.current.setContent(initialText || '');
    }
    prevTabIdRef.current = activeTabId;
    prevInitialTextRef.current = initialText;
  }, [activeTabId, initialText]);

  // Auto-save
  useEffect(() => {
    if (!activeTab || !activeTab.text || Object.keys(activeTab.note).length === 0) return;

    const lastSaved = lastSavedMapRef.current.get(activeTabId);
    if (lastSaved && activeTab.text === lastSaved.text && activeTab.title === lastSaved.title) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    autoSaveTimerRef.current = setTimeout(() => {
      handleClickRef.current(undefined, true);
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [activeTab?.text, activeTab?.title, activeTabId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + S = Save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (text) handleClick();
      }
      // Cmd/Ctrl + W = Close tab
      if ((e.metaKey || e.ctrlKey) && e.key === 'w') {
        e.preventDefault();
        if (activeTabId) closeTab(activeTabId);
      }
      // Escape = Close editor
      if (e.key === 'Escape') {
        if (confirmAction) {
          setConfirmAction(null);
        } else if (showUploadModal) {
          setShowUploadModal(false);
        } else if (showNoteMenu) {
          setShowNoteMenu(false);
        } else if (activeEditor === 1) {
          setActiveEditor(0);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [text, handleClick, activeEditor, confirmAction, showUploadModal, showNoteMenu, activeTabId, closeTab]);

  // Close note menu on outside click
  useEffect(() => {
    if (!showNoteMenu) return;
    const handleOutsideClick = (e) => {
      if (noteMenuRef.current && !noteMenuRef.current.contains(e.target)) {
        setShowNoteMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showNoteMenu]);

  const copyToClipboard = (event) => {
    navigator.clipboard.writeText(event.target.innerText);
    toast('Copied to clipboard');
  };

  useEffect(() => {
    window.copyToClipboard = copyToClipboard;
    return () => { delete window.copyToClipboard; };
    // eslint-disable-next-line
  }, []);

  const notesScrollRef = useRef(null);

  const checkNotesFade = () => {
    const scrollEl = notesScrollRef.current;
    if (!scrollEl) return;
    setNotesFadeTop(scrollEl.scrollTop > 10);
    setNotesFadeBottom(scrollEl.scrollHeight > scrollEl.clientHeight && scrollEl.scrollTop + scrollEl.clientHeight < scrollEl.scrollHeight - 10);
  };

  useEffect(() => {
    setTimeout(checkNotesFade, 100);
  }, [notes]);

  const checkFade = () => {
    const el = attachmentsRef.current;
    if (!el) return;
    setShowFadeRight(el.scrollWidth > el.clientWidth && el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
    setShowFadeLeft(el.scrollLeft > 10);
  };

  useEffect(() => {
    checkFade();
  }, [attachments]);

  // Upload files (shared between button and drag & drop)
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    let noteId = note.id;

    if (!noteId) {
      try {
        const dbObject = {
          title: title || 'Untitled note',
          text: text || '',
          category_id: category,
          date: new Date().toISOString().slice(0, 19).replace('T', ' '),
        };
        const res = await axios.post(`/notes`, dbObject);
        noteId = res.data;
        // Update current tab with the new note
        const noteRes = await axios.get(`/notes/${noteId}`);
        updateActiveTab({
          noteId: noteId,
          note: noteRes.data,
          title: noteRes.data.title,
          initialText: noteRes.data.text,
          category: noteRes.data.category_id,
        });
        setActiveEditor(1);
        fetchNotesByCategory(activeCategory, false);
      } catch (err) {
        console.log(err);
        return;
      }
    }

    const formData = new FormData();
    for (let file of files) {
      formData.append("files", file);
    }
    try {
      setUploadProgress(0);
      await axios.post(`/attachments/${noteId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      setUploadProgress(null);
      const res = await axios.get(`/attachments/${noteId}`);
      updateActiveTab({ attachments: res.data });
      fetchNotesByCategory(activeCategory, false);
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
    } catch (err) {
      setUploadProgress(null);
      console.log(err);
      toast.error(err.response?.data || 'Upload failed');
    }
  };

  const handleFileUpload = async (e) => {
    await uploadFiles(e.target.files);
    e.target.value = null;
    setShowUploadModal(false);
  };

  // Drag & drop handlers for upload modal
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    await uploadFiles(e.dataTransfer.files);
    setShowUploadModal(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget === e.target) {
      setDragOver(false);
    }
  };

  const [downloadingIds, setDownloadingIds] = useState(new Set());

  const downloadAttachment = async (id, filename) => {
    if (downloadingIds.has(id)) return;
    const startTime = Date.now();
    try {
      setDownloadingIds(prev => new Set([...prev, id]));
      const res = await axios.get(`/attachments/download/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      toast.error('Download failed');
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(300 - elapsed, 0);
      setTimeout(() => {
        setDownloadingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      }, remaining);
    }
  };

  const removeAttachment = (id) => {
    const att = attachments.find(a => a.id === id);
    const name = att?.original_name || '';
    const shortName = name.length > 30 ? name.substring(0, 27) + '...' : name;
    setConfirmAction({
      icon: <HiOutlineDocumentRemove />,
      title: "Remove attachment",
      message: `"${shortName}" will be permanently deleted.`,
      confirmText: "Remove",
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`/attachments/${id}`);
          const remaining = attachments.filter(a => a.id !== id);
          updateActiveTab({ attachments: remaining });

          // If note has no text and no attachments left, delete it
          if (!text && remaining.length === 0 && note.id) {
            await axios.put(`/notes/${note.id}`, {
              title: title || 'Untitled note',
              text: '',
              category_id: 3,
              date: new Date().toISOString().slice(0, 19).replace('T', ' '),
            });
            closeTab(activeTabId, true);
            toast.success('Empty note deleted');
          } else {
            toast.success('Attachment removed');
          }

          fetchNotesByCategory(activeCategory, false);
        } catch (err) {
          console.log(err);
        }
        setConfirmAction(null);
      }
    });
  };

  const togglePinNote = async (noteId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.patch(`/notes/${noteId}/pin`);
      const isPinned = res.data.pinned;
      toast.success(isPinned ? 'Note pinned' : 'Note unpinned');
      fetchNotesByCategory(activeCategory, false);
      // Update tab if this note is open
      const tab = tabs.find(t => t.noteId === noteId);
      if (tab) {
        updateTab(tab.id, { note: { ...tab.note, pinned: isPinned } });
      }
    } catch (err) {
      console.log(err);
      toast.error('Failed to update pin');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <HiOutlineDocument />;
    if (mimeType.startsWith('image/')) return <HiOutlinePhotograph />;
    if (mimeType.startsWith('video/')) return <HiOutlineFilm />;
    if (mimeType.startsWith('audio/')) return <HiOutlineMusicNote />;
    return <HiOutlineDocument />;
  };

  return (
    <>
    {/* Confirm popup */}
    {confirmAction && (
      <div className="confirm-overlay" onClick={() => setConfirmAction(null)}>
        <div className="confirm-popup" onClick={(e) => e.stopPropagation()}>
          <div className={`confirm-icon ${confirmAction.danger ? 'm-danger' : ''}`}>
            {confirmAction.icon}
          </div>
          <h3>{confirmAction.title}</h3>
          <p>{confirmAction.message}</p>
          <div className="confirm-buttons">
            <button className="confirm-cancel" onClick={() => setConfirmAction(null)}>Cancel</button>
            <button className={`confirm-ok ${confirmAction.danger ? 'm-danger' : ''}`} onClick={confirmAction.onConfirm}>{confirmAction.confirmText || 'Confirm'}</button>
          </div>
        </div>
      </div>
    )}

    {/* Upload modal */}
    {showUploadModal && (
      <div
        className={`confirm-overlay ${dragOver ? 'm-drag-over' : ''}`}
        onClick={() => { if (uploadProgress === null) setShowUploadModal(false); }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
          <div className={`upload-dropzone ${dragOver ? 'm-drag-over' : ''}`}>
            {uploadProgress !== null ? (
              <>
                <div className="upload-dropzone__icon">
                  <HiOutlineCloudUpload />
                </div>
                <h3>Uploading...</h3>
                <div className="upload-progress">
                  <div className="upload-progress__bar">
                    <div className="upload-progress__fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                  <span className="upload-progress__text">{uploadProgress}%</span>
                </div>
              </>
            ) : (
              <>
                <div className="upload-dropzone__icon">
                  <HiOutlineCloudUpload />
                </div>
                <h3>Upload files</h3>
                <p>Drag & drop files here or click to browse</p>
                <input type="file" ref={fileInputRef} multiple onChange={handleFileUpload} style={{ display: 'none' }} />
                <button onClick={() => fileInputRef.current.click()}>Choose files</button>
              </>
            )}
          </div>
        </div>
      </div>
    )}

    <ToastContainer
    position="bottom-right"
    autoClose={1500}
    hideProgressBar={false}
    closeOnClick={false}
    rtl={false}
    pauseOnFocusLoss
    draggable={false}
    pauseOnHover
    theme="dark"
    />
    <div id="sidebar">

        {categories && categories.length ? (

          <ul>
              {categories.map((cat, index) =>(
                <li key={cat.id} className={activeCategory === cat.id ? "m-active" : ""} onClick={ event => filterByCategory(cat.id) }>
                  <button>
                    <span className="category-icon">{cat.icon === 'clock' ? <HiOutlineClock /> : cat.icon === 'star' ? <HiOutlineStar /> : <HiOutlineTrash />}</span>
                    <small>{cat.title}</small>
                    {noteCounts[cat.id] > 0 && <em className="category-count">{noteCounts[cat.id]}</em>}
                  </button>
                </li>
              ))}
          </ul>

        ) : (
          <span></span>
        )}
    </div>
    <div id="main">
        <div id="header">
            <div className="header__search">
                <input type="text" placeholder="Search" autoComplete="off" onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="header__buttons">
                <button className={`m-link m-submit m-save ${saved === 1 ? "m-saved" : ""} ${loadingButton === 1 ? "m-loading" : ""}`} disabled={!text} onClick={event => handleClick()}><span><small>{saved === 1 ? "Saved" : "Save"}</small></span></button>
                <button className={`m-link ${activeEditor === 1 ? "m-hidden" : ""}`} onClick={event => newNote(1)}><span></span></button>
                <button className={`m-link m-close ${activeEditor === 0 ? "m-hidden" : ""}`} onClick={event => setActiveEditor(0)}><span></span></button>
            </div>
        </div>

        <div id="content">
            <div className={`b-notes ${notesFadeTop ? 'm-fade-top' : ''} ${notesFadeBottom ? 'm-fade-bottom' : ''}`}>
              <div className="notes__scroll" ref={notesScrollRef} onScroll={(e) => {
                checkNotesFade(e);
                const el = e.target;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
                  loadMoreNotes();
                }
              }}>
                <div className="notes__wrp">

                    {loadingNotes === 1 ? (
                      <>
                        <div className="b-note m-nothing m-loading"></div>
                        <div className="b-note m-nothing m-loading"></div>
                        <div className="b-note m-nothing m-loading"></div>
                        <div className="b-note m-nothing m-loading"></div>
                        <div className="b-note m-nothing m-loading"></div>
                      </>
                    ) : (

                      notes && notes.length ? (

                        notes.map(n=>(
                          <div className={`b-note ${activeNote === n.id ? "m-active" : ""} ${n.pinned ? "m-pinned" : ""} ${tabs.some(t => t.noteId === n.id) ? "m-open" : ""}`} key={n.id} onClick={ event => getNote(n.id) }>
                              <h4>{getText(n.title, 22)} {n.attachment_count > 0 && <HiOutlinePaperClip className="note__attachment-icon" />}</h4>
                              <div className="note__bottom">
                                <p>{n.text ? getText(n.text, 30) : n.attachment_count > 0 ? `${n.attachment_count} file${n.attachment_count > 1 ? 's' : ''} attached` : ''}</p>
                                <span>{timeAgo(n.date)}</span>
                              </div>
                              {n.pinned ? (
                                <button className="note__pin m-active" onClick={(e) => togglePinNote(n.id, e)} title="Unpin note"><BsPinFill /></button>
                              ) : (
                                <button className="note__pin" onClick={(e) => togglePinNote(n.id, e)} title="Pin note"><BsPin /></button>
                              )}
                          </div>
                        ))

                      ) : (
                        <div className="b-note m-nothing"><span style={{color: "#fff"}}>Nothing found</span></div>
                      )

                    )}

                    {loadingMore && (
                      <div className="b-note m-nothing m-loading"></div>
                    )}

                </div>
              </div>
            </div>
            <div className={`b-content ${activeEditor === 1 && activeTab ? "m-active" : ""}`}>
                <div className={`content__title ${loadingNote === 1 ? "m-loading" : ""}`}>
                    <div className="b-tabs">
                      {tabs.map(tab => {
                        const lastSaved = lastSavedMapRef.current.get(tab.id);
                        const isUnsaved = tab.noteId && lastSaved && (tab.text !== lastSaved.text || tab.title !== lastSaved.title);
                        return (
                          <div
                            key={tab.id}
                            className={`tab-item ${tab.id === activeTabId ? 'm-active' : ''} ${isUnsaved ? 'm-unsaved' : ''}`}
                            onClick={() => switchTab(tab.id)}
                          >
                            <span className="tab-title">{tab.title || 'Untitled'}</span>
                            {tabs.length > 1 && (
                              <button className="tab-close" onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}>
                                <HiOutlineX />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="title-input-row">
                      <input type="text" placeholder="New title" autoComplete="off" value={ title } onChange={(e) => setTitle(e.target.value)} onFocus={onFocus} />
                      <div className="b-title_buttons" ref={noteMenuRef}>
                        <button className="m-menu-toggle" onClick={() => setShowNoteMenu(!showNoteMenu)}>
                          <HiOutlineDotsVertical />
                        </button>
                        {showNoteMenu && (
                          <div className="note-menu">
                            {Object.keys(note).length !== 0 && note.category_id !== 3 && (
                              <button onClick={(e) => { togglePinNote(note.id, e); setShowNoteMenu(false); }}>
                                {note.pinned ? <BsPinFill /> : <BsPin />}
                                <span>{note.pinned ? 'Unpin note' : 'Pin note'}</span>
                              </button>
                            )}
                            {(!note.category_id || note.category_id !== 3) && (
                            <button onClick={async () => {
                              const newCat = category === 2 ? 1 : 2;
                              setCategory(newCat);
                              setShowNoteMenu(false);
                              if (Object.keys(note).length !== 0) {
                                await axios.put(`/notes/${note.id}`, {
                                  title: title || 'Untitled note',
                                  text: text,
                                  category_id: newCat,
                                  date: new Date().toISOString().slice(0, 19).replace('T', ' '),
                                });
                                toast.success(newCat === 2 ? 'Added to important' : 'Removed from important');
                                setActiveCategory(newCat);
                              }
                            }}>
                              {category === 2 ? <HiStar /> : <HiOutlineStar />}
                              <span>{category === 2 ? 'Remove from important' : 'Add to important'}</span>
                            </button>
                            )}
                            {Object.keys(note).length !== 0 && note.category_id !== 3 && (
                              <button className="m-danger" onClick={(e) => { setShowNoteMenu(false); deleteNote(e); }}>
                                <HiOutlineTrash />
                                <span>Delete note</span>
                              </button>
                            )}
                            {Object.keys(note).length !== 0 && note.category_id === 3 && (
                              <button onClick={async () => {
                                setShowNoteMenu(false);
                                try {
                                  await axios.put(`/notes/${note.id}`, {
                                    title: title || 'Untitled note',
                                    text: text,
                                    category_id: 1,
                                    date: new Date().toISOString().slice(0, 19).replace('T', ' '),
                                  });
                                  toast.success('Note restored');
                                  updateActiveTab({ category: 1, note: { ...note, category_id: 1 } });
                                  removeTab(activeTabId);
                                  fetchNotesByCategory(activeCategory, false);
                                  fetchNoteCounts();
                                } catch (err) {
                                  toast.error('Failed to restore note');
                                }
                              }}>
                                <HiOutlineReply />
                                <span>Restore note</span>
                              </button>
                            )}
                            {Object.keys(note).length !== 0 && note.category_id === 3 && (
                              <button className="m-danger" onClick={(e) => {
                                e.preventDefault();
                                setShowNoteMenu(false);
                                setConfirmAction({
                                  icon: <HiOutlineDocumentRemove />,
                                  title: "Delete permanently",
                                  message: "This note and all its attachments will be permanently deleted. This action cannot be undone.",
                                  confirmText: "Delete",
                                  danger: true,
                                  onConfirm: async () => {
                                    try {
                                      await axios.delete(`/notes/${note.id}`);
                                      toast.success('Note permanently deleted');
                                      removeTab(activeTabId);
                                      fetchNotesByCategory(activeCategory, false);
                                      fetchNoteCounts();
                                    } catch (err) {
                                      toast.error('Failed to delete note');
                                    }
                                    setConfirmAction(null);
                                  }
                                });
                              }}>
                                <HiOutlineDocumentRemove />
                                <span>Delete permanently</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                </div>
                <div className={`b-editor ${loadingNote === 1 ? "m-loading" : ""}`}>

                  {activeTab && (
                  <Editor
                    tinymceScriptSrc="/tinymce/tinymce.min.js"
                    onInit={(_evt, editor) => editorRef.current = editor}
                    init={{
                      height: '100%',
                      menubar: false,
                      statusbar: false,
                      toolbar_mode: 'sliding',
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'code', 'wordcount', 'codesample', 'fullscreen'
                      ],
                      contextmenu: false,
                      toolbar: 'undo redo | ' +
                      'bold italic underline strikethrough | forecolor backcolor | bullist numlist | fontsize blocks copyButton removeformat | alignleft aligncenter ' +
                      'alignright alignjustify | outdent indent | codesample table hr | ' +
                      'fullscreen',
                      content_css: "/editor.css?v=2",
                      font_size_formats: "8px 10px 12px 14px 16px 18px 24px 36px",
                      convert_unsafe_embeds: false,

                      setup: editor => {
                        editor.on('keydown', (e) => {
                          if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClickRef.current();
                            return false;
                          }
                        });

                        editor.ui.registry.addButton('copyButton', {
                          icon: 'copy',
                          onAction: () => {
                            const selectedText = editor.selection.getContent({ format: 'html' });
                            if (selectedText) {
                              editor.selection.setContent(`<button class="copy" onclick="parent.copyToClipboard(event)">${selectedText}</button>`);
                            }
                          }
                        });
                      },

                      extended_valid_elements: 'button[class|onclick]',

                      formats: {
                        removeformat: [
                          {
                            selector: 'b,strong,em,i,font,u,strike,s,sub,sup,dfn,code,samp,kbd,var,cite,mark,q,del,ins,small,button',
                            remove: 'all',
                            split: true,
                            block_expand: true,
                            expand: false,
                            deep: true
                          },
                          { selector: 'span', attributes: ['style', 'class'], remove: 'empty', split: true, expand: false, deep: true },
                          { selector: '*', attributes: ['style', 'class'], split: false, expand: false, deep: true }
                        ]
                      }

                    }}

                    initialValue={initialText}
                    outputFormat='text'
                    onEditorChange={(newText) => {
                      if (ignoreEditorChange.current) {
                        ignoreEditorChange.current = false;
                        return;
                      }
                      setText(newText);
                    }}

                    onFocus={onFocus}
                  />
                  )}

                </div>
                <div className={`b-attachments-wrap ${showFadeRight ? 'm-fade-right' : ''} ${showFadeLeft ? 'm-fade-left' : ''}`}>
                  <div className="b-attachments" ref={attachmentsRef} onScroll={checkFade}>
                    <button className="attachments__add" onClick={() => setShowUploadModal(true)} disabled={uploadProgress !== null}>+ Upload files</button>
                    {attachments.map(att => (
                      <div className={`attachment-item ${downloadingIds.has(att.id) ? 'm-downloading' : ''}`} key={att.id}>
                        <span className="attachment-icon">{downloadingIds.has(att.id) ? <span className="attachment-spinner"></span> : getFileIcon(att.mime_type)}</span>
                        <span className="attachment-name" onClick={() => downloadAttachment(att.id, att.original_name)}>{att.original_name}</span>
                        <span className="attachment-size">{formatFileSize(att.size)}</span>
                        <button className="attachment-remove" onClick={() => removeAttachment(att.id)}></button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="content__buttons">
                  <button className={`m-link m-submit m-save ${saved === 1 ? "m-saved" : ""} ${loadingButton === 1 ? "m-loading" : ""}`}  onClick={event => handleClick()}>{saved === 1 ? "Saved" : "Save"}</button>
                </div>
            </div>
        </div>
    </div>
    </>
  )
}

export default Home
